import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useState, useRef, useCallback } from 'react';

const libraries = ['places'];

export const AddressFields = ({ 
  formData,
  onChange,
  required = false,
  testIdPrefix = 'address'
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const [autocomplete, setAutocomplete] = useState(null);
  const [streetValue, setStreetValue] = useState(formData.street || '');

  const onLoad = useCallback((autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  }, []);

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      
      if (place.geometry) {
        const addressComponents = place.address_components || [];
        
        const getComponent = (type) => {
          const component = addressComponents.find(comp => comp.types.includes(type));
          return component ? component.long_name : '';
        };
        
        const streetNumber = getComponent('street_number');
        const route = getComponent('route');
        const street = `${streetNumber} ${route}`.trim();
        const city = getComponent('locality') || getComponent('sublocality') || getComponent('administrative_area_level_2');
        const state = getComponent('administrative_area_level_1');
        const zip = getComponent('postal_code');
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        // Update local street value
        setStreetValue(street);
        
        // Update parent form
        onChange({
          street,
          city,
          state,
          zip,
          latitude: lat,
          longitude: lng,
          formatted: place.formatted_address
        });
      }
    }
  };

  // Handle manual street input changes
  const handleStreetChange = (e) => {
    const value = e.target.value;
    setStreetValue(value);
    onChange({...formData, street: value});
  };

  // Handle individual field changes
  const handleFieldChange = (field, value) => {
    onChange({...formData, [field]: value});
  };

  if (loadError) {
    return (
      <div className="space-y-4">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-red-400 text-sm">Google Maps failed to load. Please enter address manually.</p>
        </div>
        <div>
          <Label className="text-slate-300">Street Address {required && '*'}</Label>
          <Input
            value={formData.street || ''}
            onChange={(e) => handleFieldChange('street', e.target.value)}
            className="mt-2 bg-[#1E293B] border-white/10 text-white"
            required={required}
            placeholder="123 Main St"
          />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label className="text-slate-300">City {required && '*'}</Label>
            <Input
              value={formData.city || ''}
              onChange={(e) => handleFieldChange('city', e.target.value)}
              className="mt-2 bg-[#1E293B] border-white/10 text-white"
              required={required}
            />
          </div>
          <div>
            <Label className="text-slate-300">State {required && '*'}</Label>
            <Input
              value={formData.state || ''}
              onChange={(e) => handleFieldChange('state', e.target.value.toUpperCase())}
              className="mt-2 bg-[#1E293B] border-white/10 text-white"
              required={required}
              maxLength={2}
              placeholder="OH"
            />
          </div>
          <div>
            <Label className="text-slate-300">ZIP Code {required && '*'}</Label>
            <Input
              value={formData.zip || ''}
              onChange={(e) => handleFieldChange('zip', e.target.value)}
              className="mt-2 bg-[#1E293B] border-white/10 text-white"
              required={required}
              maxLength={10}
              placeholder="43215"
            />
          </div>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
          <p className="text-blue-400 text-sm">Loading Google Maps autocomplete...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Street Address with Autocomplete */}
      <div>
        <Label htmlFor={`${testIdPrefix}-street`} className="text-slate-300">
          Street Address {required && '*'}
        </Label>
        <p className="text-xs text-[#00A6E8] mb-2">Start typing your address for suggestions</p>
        <Autocomplete
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
          options={{
            componentRestrictions: { country: 'us' },
            fields: ['address_components', 'geometry', 'formatted_address'],
            types: ['address']
          }}
        >
          <Input
            id={`${testIdPrefix}-street`}
            type="text"
            placeholder="Start typing: 123 Main Street..."
            value={streetValue}
            onChange={handleStreetChange}
            className="bg-[#1E293B] border-white/10 text-white"
            required={required}
            data-testid={`${testIdPrefix}-street-input`}
            autoComplete="off"
          />
        </Autocomplete>
      </div>

      {/* City, State, ZIP */}
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor={`${testIdPrefix}-city`} className="text-slate-300">
            City {required && '*'}
          </Label>
          <Input
            id={`${testIdPrefix}-city`}
            value={formData.city || ''}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            className="mt-2 bg-[#1E293B] border-white/10 text-white"
            required={required}
            data-testid={`${testIdPrefix}-city-input`}
            placeholder="Columbus"
          />
        </div>
        
        <div>
          <Label htmlFor={`${testIdPrefix}-state`} className="text-slate-300">
            State {required && '*'}
          </Label>
          <Input
            id={`${testIdPrefix}-state`}
            value={formData.state || ''}
            onChange={(e) => handleFieldChange('state', e.target.value.toUpperCase())}
            className="mt-2 bg-[#1E293B] border-white/10 text-white"
            required={required}
            maxLength={2}
            data-testid={`${testIdPrefix}-state-input`}
            placeholder="OH"
          />
        </div>
        
        <div>
          <Label htmlFor={`${testIdPrefix}-zip`} className="text-slate-300">
            ZIP Code {required && '*'}
          </Label>
          <Input
            id={`${testIdPrefix}-zip`}
            value={formData.zip || ''}
            onChange={(e) => handleFieldChange('zip', e.target.value)}
            className="mt-2 bg-[#1E293B] border-white/10 text-white"
            required={required}
            maxLength={10}
            data-testid={`${testIdPrefix}-zip-input`}
            placeholder="43215"
          />
        </div>
      </div>
    </div>
  );
};

// Simple City/ZIP input for Rate Calculator
export const CityZipInput = ({ 
  city,
  zip,
  onCityChange,
  onZipChange,
  required = false,
  testIdPrefix = 'rate'
}) => {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div>
        <Label htmlFor={`${testIdPrefix}-city`} className="text-slate-300">
          City {required && '*'}
        </Label>
        <Input
          id={`${testIdPrefix}-city`}
          value={city}
          onChange={(e) => onCityChange(e.target.value)}
          className="mt-2 bg-[#1E293B] border-white/10 text-white"
          required={required}
          data-testid={`${testIdPrefix}-city-input`}
          placeholder="Enter city"
        />
      </div>
      <div>
        <Label htmlFor={`${testIdPrefix}-zip`} className="text-slate-300">
          ZIP Code {required && '*'}
        </Label>
        <Input
          id={`${testIdPrefix}-zip`}
          value={zip}
          onChange={(e) => onZipChange(e.target.value)}
          className="mt-2 bg-[#1E293B] border-white/10 text-white"
          required={required}
          data-testid={`${testIdPrefix}-zip-input`}
          placeholder="Enter ZIP"
        />
      </div>
    </div>
  );
};

// Simple single-field address autocomplete for registration and other simple forms
export const SimpleAddressAutocomplete = ({
  value,
  onChange,
  placeholder = "Enter address",
  required = false,
  testId = "address-autocomplete"
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const [autocomplete, setAutocomplete] = useState(null);
  const [inputValue, setInputValue] = useState(value || '');

  const onLoad = useCallback((autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  }, []);

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      
      if (place.formatted_address) {
        setInputValue(place.formatted_address);
        onChange(place.formatted_address);
      }
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
  };

  if (loadError) {
    return (
      <Input
        value={inputValue}
        onChange={handleInputChange}
        className="mt-2 bg-[#1E293B] border-white/10 text-white"
        required={required}
        placeholder={placeholder}
        data-testid={testId}
      />
    );
  }

  if (!isLoaded) {
    return (
      <Input
        value={inputValue}
        onChange={handleInputChange}
        className="mt-2 bg-[#1E293B] border-white/10 text-white"
        required={required}
        placeholder="Loading..."
        disabled
        data-testid={testId}
      />
    );
  }

  return (
    <Autocomplete
      onLoad={onLoad}
      onPlaceChanged={onPlaceChanged}
      options={{
        componentRestrictions: { country: 'us' },
        types: ['address']
      }}
    >
      <Input
        value={inputValue}
        onChange={handleInputChange}
        className="mt-2 bg-[#1E293B] border-white/10 text-white"
        required={required}
        placeholder={placeholder}
        data-testid={testId}
      />
    </Autocomplete>
  );
};
